/**
 * Utility functions for managing search presets in local storage
 */
import i18n from 'i18n';

const PRESETS_STORAGE_KEY = 'cg_search_presets';

/**
 * Get all presets for a specific table
 * @param {string} tableId - Unique identifier for the table
 * @returns {Array} Array of preset objects
 */
export const getTablePresets = (tableId) => {
  try {
    const allPresets = JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '{}');
    return allPresets[tableId] || [];
  } catch (error) {
    console.error('Error loading presets:', error);
    return [];
  }
};

/**
 * Save a new preset for a specific table
 * @param {string} tableId - Unique identifier for the table
 * @param {string} presetName - Name of the preset
 * @param {Object} formData - Filter form data to save
 * @param {Array} filters - Filter definitions for validation
 * @returns {boolean} Success status
 */
export const saveTablePreset = (tableId, presetName, formData, filters) => {
  try {
    if (!presetName || !presetName.trim()) {
      throw new Error('Preset name is required');
    }

    // Clean form data by removing pagination and other non-filter fields
    const cleanFormData = { ...formData };
    delete cleanFormData.page;
    delete cleanFormData.limit;
    delete cleanFormData.auto;

    // Only include fields that have actual values and correspond to defined filters
    const filterNames = filters.map((f) => f.name);
    const validFormData = {};

    Object.entries(cleanFormData).forEach(([key, value]) => {
      if (
        filterNames.includes(key) &&
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        // For arrays, only include if not empty
        if (Array.isArray(value)) {
          if (value.length > 0) {
            validFormData[key] = value;
          }
        } else {
          // Include all non-null/undefined values, including boolean false and "0" strings
          validFormData[key] = value;
        }
      } else if (typeof value === 'boolean' && filterNames.includes(key)) {
        // Special case for boolean values - include even if false
        validFormData[key] = value;
      }
    });

    const allPresets = JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '{}');

    if (!allPresets[tableId]) {
      allPresets[tableId] = [];
    }

    const preset = {
      id: `preset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: presetName.trim(),
      formData: validFormData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Check if preset with same name already exists
    const existingIndex = allPresets[tableId].findIndex((p) => p.name === preset.name);
    if (existingIndex >= 0) {
      // Update existing preset
      allPresets[tableId][existingIndex] = {
        ...allPresets[tableId][existingIndex],
        ...preset,
        updatedAt: new Date().toISOString(),
      };
    } else {
      // Add new preset
      allPresets[tableId].push(preset);
    }

    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(allPresets));
    return true;
  } catch (error) {
    console.error('Error saving preset:', error);
    return false;
  }
};

/**
 * Delete a preset for a specific table
 * @param {string} tableId - Unique identifier for the table
 * @param {string} presetId - ID of the preset to delete
 * @returns {boolean} Success status
 */
export const deleteTablePreset = (tableId, presetId) => {
  try {
    const allPresets = JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '{}');

    if (!allPresets[tableId]) {
      return true; // Nothing to delete
    }

    allPresets[tableId] = allPresets[tableId].filter((preset) => preset.id !== presetId);
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(allPresets));
    return true;
  } catch (error) {
    console.error('Error deleting preset:', error);
    return false;
  }
};

/**
 * Load a specific preset
 * @param {string} tableId - Unique identifier for the table
 * @param {string} presetId - ID of the preset to load
 * @returns {Object|null} Preset data or null if not found
 */
export const loadTablePreset = (tableId, presetId) => {
  try {
    const presets = getTablePresets(tableId);
    return presets.find((preset) => preset.id === presetId) || null;
  } catch (error) {
    console.error('Error loading preset:', error);
    return null;
  }
};

/**
 * Generate a unique table ID from title, key, and optional context
 * @param {string|Object} title - Table title (string or JSX element)
 * @param {string} key - Unique table key for isolation
 * @param {string} context - Optional context (e.g., page path)
 * @returns {string} Unique table identifier
 */
export const generateTableId = (title, key = null, context = '') => {
  // Handle different title types (string, JSX element, etc.)
  let titleStr = 'Table';
  if (typeof title === 'string') {
    titleStr = title;
  } else if (title && typeof title === 'object' && title.props && title.props.children) {
    // Handle JSX elements like <span>{mode} Table</span>
    titleStr = Array.isArray(title.props.children)
      ? title.props.children.join(' ')
      : String(title.props.children);
  } else if (title) {
    titleStr = String(title);
  }

  const contextStr = typeof context === 'string' ? context : String(context || '');

  const cleanTitle = titleStr.toLowerCase().replace(/[^a-z0-9]/g, '_');
  const cleanKey = key ? key.toLowerCase().replace(/[^a-z0-9]/g, '_') : '';
  const cleanContext = contextStr.toLowerCase().replace(/[^a-z0-9]/g, '_');

  // Build ID with priority: key > title > context
  let baseId = '';
  if (cleanKey) {
    baseId = cleanKey;
  } else if (cleanTitle) {
    baseId = cleanTitle;
  }

  if (cleanContext && baseId) {
    baseId = `${cleanContext}_${baseId}`;
  } else if (cleanContext) {
    baseId = cleanContext;
  }

  // Fallback if nothing provided
  if (!baseId) {
    baseId = 'table';
  }

  // Create a simple hash for uniqueness but consistent across sessions
  const hash = baseId.split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);

  const finalId = `${baseId}_${Math.abs(hash)}`;
  return finalId;
};

/**
 * Validate if form data matches filter definitions
 * @param {Object} formData - Form data to validate
 * @param {Array} filters - Filter definitions
 * @returns {Object} Validated form data
 */
export const validatePresetFormData = (formData, filters) => {
  const validatedData = {};
  const filterNames = filters.map((f) => f.name);

  Object.entries(formData).forEach(([key, value]) => {
    if (filterNames.includes(key)) {
      const filter = filters.find((f) => f.name === key);

      // Validate based on filter type
      if (filter) {
        switch (filter.type) {
          case 'multi-select':
            validatedData[key] = Array.isArray(value) ? value : [];
            break;
          case 'select':
            // Validate that the value exists in options if options are provided
            if (filter.options && !filter.options.find((opt) => opt.value === value)) {
              validatedData[key] = '';
            } else {
              // Preserve boolean false and string values like "01", "02", etc.
              validatedData[key] = value !== null && value !== undefined ? value : '';
            }
            break;
          case 'text':
          case 'number':
          case 'date':
          default:
            validatedData[key] = value || '';
            break;
        }
      }
    }
  });

  return validatedData;
};

/**
 * Get preset summary for display
 * @param {Object} preset - Preset object
 * @param {Array} filters - Filter definitions for getting labels
 * @returns {string} Human-readable summary of the preset
 */
export const getPresetSummary = (preset, filters) => {
  if (!preset.formData || Object.keys(preset.formData).length === 0) {
    return i18n.t('common:searchPreset.noFiltersSet', 'No filters set');
  }

  const summaryParts = [];

  Object.entries(preset.formData).forEach(([key, value]) => {
    const filter = filters.find((f) => f.name === key);
    if (filter && value !== null && value !== undefined && value !== '') {
      let displayValue = value;

      if (filter.type === 'select') {
        const option = filter.options?.find((opt) => opt.value === value);
        displayValue = option ? option.label : value;
      } else if (filter.type === 'multi-select' && Array.isArray(value)) {
        if (value.length === 1) {
          const option = filter.options?.find((opt) => opt.value === value[0]);
          displayValue = option ? option.label : value[0];
        } else {
          displayValue = i18n.t('common:filter.selectedCount', { count: value.length });
        }
      }

      summaryParts.push(`${filter.label}: ${displayValue}`);
    }
  });

  return summaryParts.length > 0
    ? summaryParts.join(', ')
    : i18n.t('common:searchPreset.noFiltersSet', 'No filters set');
};

/**
 * Check if current form data matches any existing preset
 * @param {Object} formData - Current form data
 * @param {Array} presets - Array of saved presets
 * @param {Array} filters - Filter definitions
 * @returns {Object|null} Matching preset or null
 */
export const findMatchingPreset = (formData, presets, filters) => {
  if (!presets || presets.length === 0) return null;

  // Clean current form data the same way we clean when saving
  const cleanFormData = { ...formData };
  delete cleanFormData.page;
  delete cleanFormData.limit;
  delete cleanFormData.auto;

  const filterNames = filters.map((f) => f.name);
  const currentValidFormData = {};

  Object.entries(cleanFormData).forEach(([key, value]) => {
    if (
      filterNames.includes(key) &&
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          currentValidFormData[key] = value;
        }
      } else {
        currentValidFormData[key] = value;
      }
    } else if (typeof value === 'boolean' && filterNames.includes(key)) {
      // Special case for boolean values - include even if false
      currentValidFormData[key] = value;
    }
  });

  // Find a preset with matching form data
  return (
    presets.find((preset) => {
      const presetData = preset.formData || {};
      const currentKeys = Object.keys(currentValidFormData);
      const presetKeys = Object.keys(presetData);

      // If different number of keys, no match
      if (currentKeys.length !== presetKeys.length) return false;

      // Check if all keys and values match
      return currentKeys.every((key) => {
        const currentValue = currentValidFormData[key];
        const presetValue = presetData[key];

        if (Array.isArray(currentValue) && Array.isArray(presetValue)) {
          return (
            currentValue.length === presetValue.length &&
            currentValue.every((val) => presetValue.includes(val))
          );
        }

        return currentValue === presetValue;
      });
    }) || null
  );
};

/**
 * Create a clean form data object for resetting filters
 * @param {Array} filters - Filter definitions
 * @returns {Object} Clean form data with default values
 */
export const createCleanFormData = (filters) => {
  const cleanData = {};

  filters.forEach((filter) => {
    switch (filter.type) {
      case 'multi-select':
        cleanData[filter.name] = [];
        break;
      case 'select':
      case 'text':
      case 'number':
      case 'date':
      default:
        cleanData[filter.name] = '';
        break;
    }
  });

  // Always reset pagination
  cleanData.page = 1;

  return cleanData;
};

/**
 * Clear all presets from localStorage (for debugging)
 * @returns {void}
 */
export const clearAllPresets = () => {
  console.log('🗑️ [clearAllPresets] Clearing all presets from localStorage');
  const currentData = localStorage.getItem(PRESETS_STORAGE_KEY);
  console.log('🗑️ [clearAllPresets] Current localStorage data:', currentData);
  localStorage.removeItem(PRESETS_STORAGE_KEY);
  console.log('🗑️ [clearAllPresets] Cleared successfully');
};

/**
 * Get all presets data from localStorage (for debugging)
 * @returns {Object} All presets data
 */
export const getAllPresetsData = () => {
  try {
    const allData = JSON.parse(localStorage.getItem(PRESETS_STORAGE_KEY) || '{}');
    console.log('🔍 [getAllPresetsData] All presets in localStorage:', allData);
    return allData;
  } catch (error) {
    console.error('🔍 [getAllPresetsData] Error reading localStorage:', error);
    return {};
  }
};
